<template>
  <div>
    <div v-if="$apollo.loading">
      <spinner />
    </div>
    <div v-else-if="isEventCreated" class="success-container">
      <div class="success-illustration">
        <img src="/assets/successful-event-creation.png" :alt="$adminT('events.createdIllustration', 'Event created illustration')">
      </div>
      <div class="success-text">
        <h3 class="success-title">{{ $adminT('events.created', 'You have successfully created the event!') }}</h3>
        <p v-if="showCreateTicketsAction" class="success-description">
          {{ $adminT('events.ticketsLater', "You can create tickets for your event now or go to your dashboard. You'll be able to create and manage tickets from the dashboard anytime later.") }}
        </p>
        <p v-else class="success-description">
          {{ $adminT('events.manageAccreditations', 'Go to your dashboard to manage accreditations and more.') }}
        </p>
      </div>
      <div class="success-actions">
        <main-button outline :text="$adminT('events.dashboard', 'Go to dashboard')" @click="goToDashboard" />
        <main-button
          v-if="showCreateTicketsAction"
          :text="$adminT('events.createTickets', 'Create tickets now')"
          @click="goToCreateTickets"
        />
      </div>
    </div>
    <div v-else class="container">
      <span class="form-title">
        <span>{{ $adminT('events.create', 'Create') }}</span>
        <span>{{ $adminT('events.event', 'event') }}</span>
      </span>
      <form v-scroll-to-error @keydown.enter.prevent="">
        <div class="steps">
          <span :class="activeStep === 1 ? 'curr-step' : 'prev-step'" @click="activeStep = 1">
            {{ $adminT('events.stepInfo', '1. Event Info') }}
          </span>
          <hr :class="activeStep > 1 ? 'hr-active' : 'hr'">
          <span
            :class="['step', activeStep === 2 && 'curr-step', activeStep === 3 && 'prev-step']"
            @click=";(activeStep = 2), moduleCheck()"
          >
            {{
              isRegularOrganization() || isSportOrganization() ? $adminT('events.stepModules', '2. Modules') : $adminT('events.stepDates', '2. Dates & Tickets')
            }}
          </span>
        </div>
        <div v-if="activeStep === 1">
          <event-info-form
            v-if="isSportOrganization() || isRegularOrganization()"
            :vs="$v.form.generalInfo"
            :initial-values="form.generalInfo"
            :categories="allEventCategories"
            @next="activeStep = 2"
            @values="form.generalInfo = $event"
          />
          <theater-event-info-form
            v-else
            :vs="$v.form.generalInfo"
            :initial-values="form.generalInfo"
            :organization-slug="organizationSlug"
            :categories="allEventCategories"
            @next="activeStep = 2"
            @values="form.generalInfo = $event"
          />
        </div>
        <div v-if="activeStep === 2">
          <event-extra-modules
            v-if="isRegularOrganization() || isSportOrganization()"
            :initial-values="form"
            :vs="$v.form"
            :vat-obligor="vatObligor"
            :submitting-form="submittingForm"
            @back="activeStep = 1"
            @submit="submitForm()"
          />
          <performance-container
            v-else
            :initial-values="form.performance"
            :organization-slug="organizationSlug"
            :vs="$v.form.performance"
            :submitting-form="submittingForm"
            :vat-percentage.sync="form.generalInfo.VATPercentage"
            @removePerformance="form.performance = $event"
            @back="activeStep = 1"
            @createPerformance="handleCreatePerformance"
            @venueChange="venues = $event"
          />
        </div>
      </form>
    </div>
    <simple-snackbar :open.sync="snackbar.open" :color="snackbar.color" :message-prop="workflowLabel(localizedSnackbarMessage)" />
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import {
  between,
  maxLength,
  minLength,
  minValue,
  required,
  requiredIf,
} from 'vuelidate/lib/validators'

import { GET_ALL_EVENT_CATEGORIES } from '@/graphql/eventCategory.graphql'
import { GET_ORGANIZATION_BY_SLUG } from '@/graphql/organization.graphql'
import { CREATE_ANNUAL_TICKETS_FOR_EVENT, CREATE_EVENT } from '@/graphql/event.graphql'
import { date, httpUrl, maxDate, minDate } from '@/validators'
import { submitGuard, tvornicaKultureCustomOrganizerMsg } from '@/utils'
import { CategoryType, EventCategoryId, OrganizationSlug } from '@/types'

import TheaterEventInfoForm from '@/components/events/theaterEventInfoForm.vue'
import eventInfoForm from './event-info-form.vue'
import eventExtraModules from './event-extra-modules.vue'
import performanceContainer from './performance-container.vue'
import simpleSnackbar from '../shared/snackbars/simple-snackbar.vue'
import { CREATE_EVENT_TEMPLATE_AND_PERFORMANCE } from '@/graphql/eventTemplate.graphql'
import moment from 'moment'

export default {
  mixins: [WorkflowDisplay],
  name: 'event-create-container',
  components: {
    eventInfoForm,
    eventExtraModules,
    TheaterEventInfoForm,
    simpleSnackbar,
    performanceContainer,
  },
  props: {
    organizationSlug: {
      type: String,
    },
  },
  validations() {
    return {
      form: {
        generalInfo: {
          eventCategoryId: { required },
          interests: {
            required: requiredIf(() => this.isSubcategoryRequiredField),
          },
          title: { required, maxLength: maxLength(120) },
          instagramLink: {
            httpUrl,
          },
          webLink: {
            httpUrl,
          },
          facebookLink: {
            httpUrl,
          },
          genreId: {
            required: requiredIf(
              () => !this.isSportOrganization() && !this.isRegularOrganization(),
            ),
          },
          durationInMins: {
            required: requiredIf(
              () => !this.isSportOrganization() && !this.isRegularOrganization(),
            ),
          },
          from: {
            required: requiredIf(() => this.isSportOrganization() || this.isRegularOrganization()),
            date,
            minDate: minDate(() => new Date()),
            maxDate: maxDate(() => this.form.generalInfo.to),
          },
          to: {
            required: requiredIf(() => this.isSportOrganization() || this.isRegularOrganization()),
            date,
            minDate: minDate(() => this.form.generalInfo.from),
          },
          location: {
            venue: {
              required: requiredIf(
                () => this.isSportOrganization() || this.isRegularOrganization(),
              ),
            },
            address1: {
              required: requiredIf(
                () => this.isSportOrganization() || this.isRegularOrganization(),
              ),
            },
          },
          highlightedData: {
            ageRestriction: {
              value: {
                required: requiredIf(
                  () => this.form.generalInfo.highlightedData.ageRestriction.enabled,
                ),
                minValue: minValue(0),
              },
            },
            freeEntranceWithAgeLimit: {
              value: {
                required: requiredIf(
                  () => this.form.generalInfo.highlightedData.freeEntranceWithAgeLimit.enabled,
                ),
                minValue: minValue(0),
              },
            },
          },
          faqs: {
            $each: {
              answer: { required },
              question: { required },
            },
          },
          metadata: {
            webLink: {
              httpUrl,
            },
            facebookLink: {
              httpUrl,
            },
          },
        },
        numberOfZones: {
          required: requiredIf(({ modules }) => modules && modules.categoriesAndZones),
          between: between(1, 10),
        },
        performance: {
          required: requiredIf(() => !this.isSportOrganization() && !this.isRegularOrganization()),
          $each: {
            date: {
              required: requiredIf(
                () => !this.isSportOrganization() && !this.isRegularOrganization(),
              ),
            },
            time: {
              required: requiredIf(
                () => !this.isSportOrganization() && !this.isRegularOrganization(),
              ),
            },
            performanceType: {
              required: requiredIf(
                () => !this.isSportOrganization() && !this.isRegularOrganization(),
              ),
            },
            venueId: {
              required: requiredIf(
                () => !this.isSportOrganization() && !this.isRegularOrganization(),
              ),
            },
            orderItemTemplateIds: {
              required: requiredIf(
                () => !this.isSportOrganization() && !this.isRegularOrganization(),
              ),
            },
            //TODO find a way to handle this
            audienceCapacity: {
              required(v, obj) {
                let hasNoNumeration = this.venues.length
                  ? this.venues
                    .filter(v => !v.seats || !v.predefinedSections)
                    .map(v => v.id)
                    .includes(obj.venueId)
                  : false

                return hasNoNumeration ? required(v) : required(!v)
              },
            },
            additionalCapacity: {
              required(v, obj) {
                let hasNoNumeration = !!this.venues.find(
                  v => v.id === obj.venueId && (!v.seats || !v.predefinedSections),
                )

                return hasNoNumeration && obj.addExtraSeats ? required(v) : required(!v)
              },
              minValue: (value, p) => !p.addExtraSeats || value >= 1,
            },
            // orderItemTemplateIds: {
            //   minLength: minLength(1),
            // },
          },
        },
        categories: {
          $each: {
            form: {
              name: { required },
              color: { required },
              zones: {
                required,
                minLength: minLength(1),
                maxLength: maxLength(10),
              },
            },
          },
        },
      },
    }
  },
  data: () => ({
    snackbar: { open: false, color: 'primary', messageProp: '' },
    activeStep: 1,
    CategoryType,
    EventCategoryId,
    isSubcategoryRequiredField: false,
    form: {
      generalInfo: {
        title: null,
        from: null,
        to: null,
        metadata: {
          webLink: '',
          facebookLink: '',
        },
        eventCategoryId: null,
        interests: null,
        location: {
          latitude: 45.8087407,
          longitude: 15.9740865,
        },
        cover: null,
        description: null,
        organizerMessage: null,
        highlightedData: {
          ageRestriction: {
            show: false,
            enabled: false,
            value: 0,
          },
          freeEntranceWithAgeLimit: {
            show: false,
            enabled: false,
            value: 0,
          },
          tableReservation: {
            show: false,
            enabled: false,
          },
        },
        faqs: [],
      },
      performance: [],
      categories: [],
      modules: {
        ticketPersonalization: false,
      },
      // Tax class picked in the modules step; that step receives the whole form
      // as initial-values and writes the selected rate here, not into generalInfo.
      VATPercentage: null,
      numberOfZones: 1,
    },
    allEventCategories: [],
    venues: [],
    vatObligor: false,
    submittingForm: false,
    createdEventId: null,
    createdEventTemplateId: null,
  }),
  computed: {
    localizedSnackbarMessage() {
      switch (this.snackbar && this.snackbar.messageProp) {
      case 'At least one performance is required.': return this.$adminT('events.performanceRequired', 'At least one performance is required.')
      case 'Fill in the required information': return this.$adminT('events.requiredInfo', 'Fill in the required information')
      case 'Oops, Something went wrong!': return this.$adminT('events.error', 'Oops, Something went wrong!')
      default: return this.snackbar && this.snackbar.messageProp
      }
    },

    hasTicketing() {
      if (!this.isRegularOrganization() && !this.isSportOrganization()) {
        return true
      }

      return !!(this.form.modules && this.form.modules.ticketing)
    },
    showCreateTicketsAction() {
      return this.hasTicketing && (this.isRegularOrganization() || this.isSportOrganization())
    },
    isEventCreated() {
      return !!this.createdEventId || !!this.createdEventTemplateId
    },
  },
  apollo: {
    getOrganizationBySlug: {
      query: GET_ORGANIZATION_BY_SLUG,
      variables() {
        return { organizationSlug: this.organizationSlug }
      },
      skip() {
        return !this.organizationSlug
      },
      result({ data, loading }) {
        if (!loading && data && data.getOrganizationBySlug) {
          this.vatObligor = !!data.getOrganizationBySlug.VATObligor
          // Theater template VAT default: obligors start toggled on at 5%, others at 0% (untoggled).
          if (this.form.generalInfo.VATPercentage == null) {
            this.$set(this.form.generalInfo, 'VATPercentage', this.vatObligor ? 5 : 0)
          }
        }
      },
    },
    getAllEventCategories: {
      query: GET_ALL_EVENT_CATEGORIES,
      result({ data, loading }) {
        if (!loading && data) {
          if (this.isRegularOrganization() || this.isSportOrganization()) {
            this.allEventCategories = data.getAllEventCategories.filter(
              category => category.name !== this.CategoryType.THEATER,
            )
          } else {
            this.allEventCategories = data.getAllEventCategories.sort((a, b) => a.id - b.id)
          }
        }
      },
    },
  },
  watch: {
    activeStep() {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    'form.generalInfo.eventCategoryId': function () {
      const category = this.allEventCategories.find(
        c => c.id === this.form.generalInfo.eventCategoryId,
      )
      if (!category) {
        return (this.isSubcategoryRequiredField = false)
      }
      const subCategories = [...category.subCategories].filter(sc => !sc.organizationId)

      return (this.isSubcategoryRequiredField = subCategories.length)
    },
  },
  mounted() {
    if (this.isSportOrganization()) {
      this.form.modules = { ...this.form.modules, ticketing: true }
    }
    if (this.organizationSlug === OrganizationSlug.TVORNICA_KULTURE) {
      this.form.generalInfo.organizerMessage = tvornicaKultureCustomOrganizerMsg
    }
  },
  methods: {
    async handleCreatePerformance(performanceInfo) {
      //todo fix validation , add input error colors within performances
      //todo fix bug on category change - second step doesn't work properly

      this.$v.form.$touch()

      if (!this.$v.form.performance.$model.length) {
        return (this.snackbar = {
          open: true,
          messageProp: 'workflowsAll.at_least_one_performance_is_required_fb83b93',
          color: 'error',
        })
      }

      if (this.$v.form.$invalid === true) {
        return (this.snackbar = {
          open: true,
          messageProp: 'workflowsAll.fill_in_the_required_information_a32c330',
          color: 'error',
        })
      }
      await submitGuard(this, async () => {
        this.submittingForm = true
        const performanceInput = this.performanceDataEdit(performanceInfo)

        // delete this.form.generalInfo.metadata
        this.form.generalInfo.eventCategoryId = Number(this.form.generalInfo.eventCategoryId)
        const { to, from, location, organizerMessage, metadata, ...templateInfo } =
          this.form.generalInfo

        const eventTemplateInput = { ...templateInfo }

        // Metadata object should not be in the request in case of theater organization
        if (this.isSportOrganization() || this.isRegularOrganization()) {
          Object.assign(eventTemplateInput, metadata)
        }

        const { errors, data } = await this.$apollo.mutate({
          mutation: CREATE_EVENT_TEMPLATE_AND_PERFORMANCE,
          variables: {
            organizationSlug: this.organizationSlug,
            eventTemplateInput,
            performanceInput,
          },
        })
        if (errors) {
          this.snackbar = {
            open: true,
            messageProp: 'workflowsAll.oops_something_went_wrong_74c86ae',
            color: 'error',
          }
          this.submittingForm = false
        } else {
          this.createdEventTemplateId = data.createEventTemplateAndPerformance
          this.submittingForm = false
        }
      }).catch(() => {
        this.snackbar = {
          open: true,
          messageProp: 'workflowsAll.oops_something_went_wrong_74c86ae',
          color: 'error',
        }
        this.submittingForm = false
      })
    },
    performanceDataEdit(data) {
      return data.map(data => {
        const { time, date, addExtraSeats, ...info } = data
        const formatedDate = moment(date).format('YYYY-MM-DD')

        return {
          ...info,
          from: moment(`${formatedDate} ${time}`),
        }
      })
    },
    moduleCheck() {
      if (!this.form.generalInfo.eventCategoryId) return

      if (
        this.allEventCategories.find(c => parseInt(c.id) === this.form.generalInfo.eventCategoryId)
          .name === this.CategoryType.MUSIC
      ) {
        this.form.modules = {
          ...this.form.modules,
          ticketing: true,
          categoriesAndZones: this.ticketing ? false : this.categoriesAndZones,
        }
      }
    },
    async submitForm() {
      this.$v.form.$touch()

      if (this.$v.form.$invalid === true) {
        return (this.snackbar = {
          open: true,
          messageProp: 'workflowsAll.fill_in_the_required_information_a32c330',
          color: 'error',
        })
      }
      await submitGuard(this, async () => {
        this.submittingForm = true
        const values = this.form

        const { categories: categoriesForm, generalInfo, modules, numberOfZones } = values

        const {
          to,
          from,
          location,
          title,
          description,
          cover,
          interests,
          metadata,
          highlightedData,
          faqs,
          eventCategoryId,
        } = generalInfo

        // The modules step is the tax class picker for this flow, so its value wins;
        // generalInfo only carries the organization default (used by the theater flow).
        const VATPercentage =
          values.VATPercentage != null ? values.VATPercentage : generalInfo.VATPercentage

        const baseEventData = {
          title,
          to,
          from,
          numberOfZones,
          description,
          cover,
          metadata,
          highlightedData,
          faqs,
          ...(VATPercentage != null ? { VATPercentage: Number(VATPercentage) } : {}),
        }

        const extraModules = {
          categoriesAndZones: modules && modules.categoriesAndZones,
          mobileApp: modules && modules.mobileApp,
          conferenceMobileApp: modules && modules.conferenceMobileApp,
          expoMobileApp: modules && modules.expoMobileApp,
          workshops: modules && modules.workshops,
          ticketing: modules && !modules.categoriesAndZones && modules.ticketing,
          floorPlan: modules && modules.floorPlan,
          ticketPersonalization: !!(modules && modules.ticketPersonalization),
        }
        const categories = (categoriesForm || []).map(({ form }) => form)
        const {
          errors,
          data: {
            createEvent: { id },
          },
        } = await this.$apollo.mutate({
          mutation: CREATE_EVENT,
          variables: {
            event: {
              baseEventData,
              interests,
              eventCategoryId,
              location,
              extraModules,
              categories,
            },
            organizationSlug: this.organizationSlug,
          },
          refetchQueries: ['getEventsAndQuota', 'getOrganization', 'getOrganizations'],
        })

        if (errors && errors[0]) {
          this.snackbar = {
            open: true,
            messageProp: errors[0].message,
            color: 'error',
          }
          this.submittingForm = false
        } else {
          if (this.isSportOrganization()) {
            await this.$apollo.mutate({
              mutation: CREATE_ANNUAL_TICKETS_FOR_EVENT,
              variables: { eventId: id },
            })
          }
          this.createdEventId = id
          this.submittingForm = false
        }
      })
    },
    goToDashboard() {
      if (this.isSportOrganization()) {
        const routeName = this.hasTicketing
          ? this.routes.sportOrganizer.eventDashboardSport
          : this.routes.sportOrganizer.accreditationsSport
        this.goTo(routeName, {
          organizationSlug: this.organizationSlug,
          eventId: this.createdEventId,
        })
      } else if (this.isRegularOrganization()) {
        const routeName = this.hasTicketing
          ? this.routes.organizer.eventDashboard
          : this.routes.organizer.accreditations
        this.goTo(routeName, {
          organizationSlug: this.organizationSlug,
          eventId: this.createdEventId,
        })
      } else {
        this.goTo(this.routes.theaterOrganizer.eventDashboardTheater, {
          organizationSlug: this.organizationSlug,
          eventTemplateId: this.createdEventTemplateId,
        })
      }
    },
    goToCreateTickets() {
      if (this.isSportOrganization()) {
        this.goTo(this.routes.sportOrganizer.ticketsSport, {
          organizationSlug: this.organizationSlug,
          eventId: this.createdEventId,
        })
      } else if (this.isRegularOrganization()) {
        this.goTo(this.routes.organizer.tickets, {
          organizationSlug: this.organizationSlug,
          eventId: this.createdEventId,
        })
      } else {
        this.goTo(this.routes.theaterOrganizer.ticketsTheater, {
          organizationSlug: this.organizationSlug,
          eventTemplateId: this.createdEventTemplateId,
        })
      }
    },
    isRegularOrganization() {
      return this.$route.path.includes('/organizations/')
    },
    isSportOrganization() {
      return this.$route.path.includes('/sport-organizations/')
    },
  },
}
</script>

<style scoped>
.form-title {
  height: fit-content;
  color: #ff4c00;
  display: flex;
  flex-direction: column;
  font-family: 'Raleway', sans-serif;
  font-size: 2.25rem;
  font-weight: bold;
  margin-right: 3rem;
}

.container {
  padding-top: 0.75rem !important;
  display: flex;
}

.steps {
  font-family: 'Raleway';
  margin-bottom: 2rem;
  font-style: normal;
  font-weight: 600;
  font-size: 1.5rem;
  color: #d9d9d9;
  cursor: pointer;
}

.curr-step {
  font-family: 'Raleway';
  cursor: pointer;
  font-style: normal;
  font-weight: 600;
  font-size: 1.5rem;
  color: #0277bd !important;
  margin-bottom: 0.75rem;
}

.prev-step {
  cursor: pointer;
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 1.5rem;
  color: #979797 !important;
}

.hr {
  width: 2.25rem;
  height: 0.25rem;
  display: inline-block;
  background: #d9d9d9;
  border: none;
  border-radius: 0.5rem;
  vertical-align: middle;
  margin: 0 0.75rem;
  cursor: default;
}

.hr-active {
  width: 2.25rem;
  height: 0.25rem;
  border: none;
  border-radius: 0.5rem;
  display: inline-block;
  background: #979797;
  vertical-align: middle;
  cursor: default;
  margin: 0 0.75rem;
}

@media screen and (max-width: 960px) {
  .container {
    display: flex;
    padding: 2rem;
    flex-direction: column;
  }

  .form-title {
    margin-bottom: 1.5rem;
    column-gap: 0.5rem;
    flex-direction: row;
  }
}

@media screen and (min-width: 1904px) {
  .container {
    max-width: 74.063rem !important;
  }
}

form {
  width: min(43.52rem, 100%);
}

.success-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3rem;
  padding: 3rem 1.5rem;
  max-width: 43.52rem;
  margin: 0 auto;
}

.success-illustration {
  width: 16.625rem;
  height: 18.75rem;
}

.success-illustration img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.success-text {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  text-align: center;
  width: 100%;
}

.success-title {
  font-family: 'Raleway', sans-serif;
  font-weight: 800;
  font-size: 2.25rem;
  line-height: 1.2;
  letter-spacing: -1.08px;
  color: #004c8c;
  font-feature-settings: 'pnum' on, 'lnum' on;
  margin: 0;
}

.success-description {
  font-family: 'Source Sans Pro', sans-serif;
  font-weight: 400;
  font-size: 1.25rem;
  line-height: 1.5;
  color: #666;
  margin: 0;
}

.success-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  width: 100%;
}

@media screen and (max-width: 960px) {
  .success-container {
    gap: 2rem;
    padding: 2rem 1rem;
  }

  .success-illustration {
    width: 12rem;
    height: 13.5rem;
  }

  .success-title {
    font-size: 1.5rem;
    letter-spacing: -0.72px;
  }

  .success-description {
    font-size: 1rem;
  }

  .success-actions {
    flex-direction: column;
  }
}
</style>
